/**
 * Ask the project wiki a question with a context budget; the answer cites wiki pages, documents, and Sources, can be
 * saved to the wiki as a saved question, and the page previews what an agent would get for it (child route of the shell)
 */

import { useMemo, useState, type FormEvent, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { ASK_CREDITS, checkScope, PERMISSIONS, type AskResult } from '@agentwiki/shared'
import { useAuth } from '../hooks/use-auth'
import { useAsk, useSaveAsk } from '../hooks/use-health-ask'
import { useProjectContext } from '../hooks/use-project-content'
import { ApiError } from '../lib/api-client'
import { renderAnswerCitations } from '../lib/wiki-citations'
import { WikiMarkdown } from '../components/wiki/wiki-markdown'
import { projectPath } from '../components/shell/nav-config'
import { badgeClass, buttonPrimaryClass, buttonSecondaryClass, errorTextClass, hintClass, inputClass, labelClass } from '../components/ui/styles'
import { useT, type TranslationKey } from '../i18n'

/** Context budgets offered for an answer, in tokens */
export const ASK_BUDGETS = [2000, 4000, 6000, 8000] as const
/** Same as the server default when no budget is sent, so the web does not answer with less context than before */
const DEFAULT_ASK_BUDGET = 6000

const KIND_KEY: Record<AskResult['citations'][number]['kind'], TranslationKey> = {
  wiki: 'ask.kind.wiki',
  source: 'ask.kind.source',
  document: 'ask.kind.document',
}

/** A provider failure answers 502 with the stored result; read it back from the error */
function failedResult(err: unknown): AskResult | null {
  if (!(err instanceof ApiError) || err.status !== 502) return null
  const data = err.data as Partial<AskResult> | null
  return data && data.status === 'failed' ? (data as AskResult) : null
}

/** One Ask result: the cited answer (no images, remote links as plain text), or why there is none */
export function AskAnswer({ result, actions }: { result: AskResult; actions?: ReactNode }) {
  const t = useT()
  const paths = useMemo(() => new Set(result.citations.map((c) => c.path)), [result.citations])
  const markdown = useMemo(() => renderAnswerCitations(result.answer ?? '', result.citations), [result])

  if (result.status === 'not_found') {
    return (
      <div className="space-y-1 rounded-xl border border-dashed border-border p-4">
        <p className="font-medium text-fg">{t('ask.notFound')}</p>
        <p className={hintClass}>{t('ask.notFoundHint')}</p>
      </div>
    )
  }
  if (result.status === 'failed') {
    return <p role="alert" className={errorTextClass}>{t('ask.providerFailed', { message: result.error ?? '' })}</p>
  }
  return (
    <article className="space-y-4 rounded-xl border border-border bg-surface-raised p-4 md:p-5">
      <WikiMarkdown content={markdown} isCitation={(href) => paths.has(href)} untrusted />
      {result.citations.length > 0 && (
        <section className="space-y-2 border-t border-border pt-3">
          <h2 className="text-sm font-semibold text-fg-subtle">{t('ask.citations')}</h2>
          <ol className="space-y-1 text-sm">
            {result.citations.map((c, i) => (
              <li key={`${c.kind}:${c.ref}`} className="flex flex-wrap items-baseline gap-2">
                <span className="tabular-nums text-fg-subtle">[{i + 1}]</span>
                <span className={badgeClass}>{t(KIND_KEY[c.kind])}</span>
                <Link to={c.path} className="text-accent-text hover:underline">{c.title}</Link>
              </li>
            ))}
          </ol>
        </section>
      )}
      <div className="flex flex-col gap-2 border-t border-border pt-3 md:flex-row md:items-center md:justify-between">
        <p className={hintClass}>{t('ask.charged', { credits: result.creditsCharged })}</p>
        {actions}
      </div>
    </article>
  )
}

/** Save an answered question to the wiki; the toast links to the new page, or to the review queue when it waits */
function SaveToWikiButton({ project, result }: { project: string; result: AskResult }) {
  const t = useT()
  const navigate = useNavigate()
  const save = useSaveAsk(project)
  const [savedId, setSavedId] = useState<string | null>(null)
  const saved = savedId === result.id

  const onSave = () => {
    save.mutate(result.id, {
      onSuccess: (written) => {
        setSavedId(result.id)
        const target = written.status === 'applied' && written.page
          ? { message: t('ask.saved'), label: t('ask.openPage'), path: projectPath(project, `wiki/${encodeURIComponent(written.page.slug)}`) }
          : { message: t('ask.savedPending'), label: t('ask.openReview'), path: projectPath(project, 'sources?tab=review') }
        toast.success(target.message, { action: { label: target.label, onClick: () => navigate(target.path) } })
      },
      onError: (err) => toast.error(t('ask.saveFailed', { message: err instanceof Error ? err.message : String(err) })),
    })
  }

  return (
    <button type="button" onClick={onSave} disabled={save.isPending || saved} title={t('ask.saveHint')} className={buttonSecondaryClass}>
      {save.isPending ? t('ask.saving') : saved ? t('ask.savedButton') : t('ask.save')}
    </button>
  )
}

/** The packed context an agent would get for the same question and budget (free, read-only) */
function AgentContextPreview({ project, question, budget }: { project: string; question: string; budget: number }) {
  const t = useT()
  const context = useProjectContext(project)
  const [open, setOpen] = useState(false)
  const toggle = () => {
    if (!open) context.mutate({ task: question, budget, includeSources: true })
    setOpen(!open)
  }
  return (
    <section className="space-y-2">
      <button type="button" onClick={toggle} aria-expanded={open} className={buttonSecondaryClass}>
        {open ? t('ask.previewHide') : t('ask.preview')}
      </button>
      {open && (
        <div className="space-y-2">
          <p className={hintClass}>{t('ask.previewHint')}</p>
          {context.isPending && <p className={hintClass}>{t('ask.previewLoading')}</p>}
          {context.error && <p role="alert" className={errorTextClass}>{t('ask.previewFailed', { message: context.error.message })}</p>}
          {context.data && (
            <>
              <p className={hintClass}>{t('ask.previewResult', { items: context.data.items.length, used: context.data.usedTokens, budget: context.data.budget })}</p>
              <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-surface-muted p-3 text-xs leading-relaxed text-fg">
                {context.data.markdown}
              </pre>
            </>
          )}
        </div>
      )}
    </section>
  )
}

export function ProjectAskPage() {
  const t = useT()
  const { projectSlug = 'default' } = useParams<{ projectSlug: string }>()
  const [question, setQuestion] = useState('')
  const [budget, setBudget] = useState<number>(DEFAULT_ASK_BUDGET)
  // One id per question text: submitting the same question again (a retry) returns the first answer, uncharged
  const [requestId, setRequestId] = useState(() => crypto.randomUUID())
  const ask = useAsk(projectSlug)
  // Asking spends workspace credits: editors and admins only, like a server compile
  const { role } = useAuth()
  const canAsk = role ? checkScope(PERMISSIONS[role] ?? [], 'doc:update') : false

  const submit = (event: FormEvent) => {
    event.preventDefault()
    const text = question.trim()
    if (!canAsk || text.length < 3 || ask.isPending) return
    ask.mutate({ question: text, requestId, budget }, {
      // The provider failed and nothing was charged: asking again is a new attempt, not a retry of a lost response
      onError: (err) => {
        if (failedResult(err)) setRequestId(crypto.randomUUID())
      },
    })
  }

  const failed = failedResult(ask.error)
  const result = ask.data ?? failed

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6 px-4 py-6 md:px-8 md:py-10">
      <header className="space-y-1">
        <h1 className="font-display text-2xl font-semibold tracking-tight text-fg">{t('ask.title')}</h1>
        <p className="max-w-2xl text-sm text-fg-muted">{t('ask.subtitle')}</p>
      </header>

      <form onSubmit={submit} className="space-y-2">
        <label htmlFor="ask-question" className={labelClass}>{t('ask.label')}</label>
        <textarea
          id="ask-question"
          value={question}
          maxLength={1000}
          rows={3}
          onChange={(e) => {
            setQuestion(e.target.value)
            setRequestId(crypto.randomUUID())
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit(e)
          }}
          placeholder={t('ask.placeholder')}
          disabled={!canAsk}
          aria-describedby={canAsk ? undefined : 'ask-needs-editor'}
          className={inputClass}
        />
        {!canAsk && <p id="ask-needs-editor" className={hintClass}>{t('ask.needsEditor')}</p>}
        <div className="flex flex-col gap-1 md:flex-row md:items-center md:gap-3">
          <label htmlFor="ask-budget" className={labelClass}>{t('ask.budget')}</label>
          <select
            id="ask-budget"
            value={budget}
            disabled={!canAsk}
            onChange={(e) => {
              setBudget(Number(e.target.value))
              // A different budget is a different question to answer, not a retry
              setRequestId(crypto.randomUUID())
            }}
            className={`${inputClass} md:w-40`}
            aria-describedby="ask-budget-hint"
          >
            {ASK_BUDGETS.map((tokens) => (
              <option key={tokens} value={tokens}>{t('ask.budgetOption', { tokens: tokens / 1000 })}</option>
            ))}
          </select>
          <p id="ask-budget-hint" className={hintClass}>{t('ask.budgetHint')}</p>
        </div>
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <p className={hintClass}>{t('ask.cost', { credits: ASK_CREDITS })}</p>
          <button type="submit" disabled={!canAsk || ask.isPending || question.trim().length < 3} className={buttonPrimaryClass}>
            {ask.isPending ? t('ask.asking') : t('ask.submit', { credits: ASK_CREDITS })}
          </button>
        </div>
      </form>

      {ask.error && !failed && (
        <p role="alert" className={errorTextClass}>{t('ask.failed', { message: ask.error.message })}</p>
      )}
      {result && !ask.isPending && (
        <AskAnswer
          result={result}
          actions={canAsk && result.status === 'answered' ? <SaveToWikiButton key={result.id} project={projectSlug} result={result} /> : undefined}
        />
      )}
      {result && !ask.isPending && (
        <AgentContextPreview key={`${result.id}:${budget}`} project={projectSlug} question={result.question} budget={budget} />
      )}
    </div>
  )
}
